import { motion } from 'motion/react';
import { FormEvent, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../components/AuthProvider';
import { ApiError, apiFetch } from '../lib/api';
import { isAdminLoginPath, isAdminLoginShortcut, logoTapsOpenAdmin } from '../lib/admin-login-entry';

export function LoginPage() {
  const { signIn, refresh } = useAuth();
  const location = useLocation();
  const [adminOpen, setAdminOpen] = useState(() => isAdminLoginPath(location.pathname, location.search));
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const logoTaps = useRef<number[]>([]);

  useEffect(() => {
    if (isAdminLoginPath(location.pathname, location.search)) setAdminOpen(true);
  }, [location.pathname, location.search]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (!isAdminLoginShortcut(event)) return;
      event.preventDefault();
      setAdminOpen(true);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const onLogoTap = () => {
    const result = logoTapsOpenAdmin(logoTaps.current, Date.now());
    logoTaps.current = result.taps;
    if (result.open) setAdminOpen(true);
  };

  const submitAdmin = async (event: FormEvent) => {
    event.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await apiFetch('/api/auth/password', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      });
      setPassword('');
      await refresh();
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
        setError('Too many attempts. Try again later.');
      } else if (err instanceof ApiError) {
        setError('Invalid email or password.');
      } else {
        setError('Could not sign in. Try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--color-surface-0)] p-6">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
        className="w-full max-w-sm bg-[var(--color-surface-1)] rounded-[var(--radius-xl)] p-8 md:p-10 shadow-[var(--shadow-lg)] border border-[var(--color-border)]"
      >
        <div className="text-center mb-8">
          <div className="w-20 h-20 mx-auto mb-6">
            <img
              src="/logo-ui.png?v=20261103"
              alt="SMYM Chennai"
              className="w-full h-full object-contain"
              onClick={onLogoTap}
            />
          </div>
          <h1 className="text-2xl font-bold text-[var(--color-text-primary)] mb-2">SMYM Chennai</h1>
          <p className="text-[var(--color-text-secondary)] text-sm leading-relaxed">
            Welcome to the official app for Shree Maheshwari Yuva Mandal, Chennai.
          </p>
        </div>

        <button
          type="button"
          onClick={signIn}
          className="w-full flex items-center justify-center gap-3 bg-[var(--color-surface-1)] hover:bg-[var(--color-surface-2)] text-[var(--color-text-primary)] font-medium py-3 px-6 border border-[var(--color-border)] rounded-[var(--radius-md)] transition-colors duration-[var(--duration-fast)] shadow-[var(--shadow-sm)]"
        >
          <img src="/google-g.png" alt="Google" className="w-5 h-5" />
          Continue with Google
        </button>

        {adminOpen ? (
          <form id="admin-email-signin" method="post" action="#" onSubmit={(event) => void submitAdmin(event)} className="mt-5 space-y-3">
            <div>
              <label htmlFor="admin-email" className="block text-xs font-medium text-[var(--color-text-secondary)] mb-1.5">
                Email
              </label>
              <input
                id="admin-email"
                name="username"
                type="email"
                autoComplete="username"
                autoFocus
                required
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                className="w-full bg-[var(--color-surface-1)] border border-[var(--color-border)] rounded-[var(--radius-md)] py-2.5 px-3 text-sm outline-none focus:border-[var(--color-accent)]"
              />
            </div>
            <div>
              <label htmlFor="admin-password" className="block text-xs font-medium text-[var(--color-text-secondary)] mb-1.5">
                Password
              </label>
              <input
                id="admin-password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                className="w-full bg-[var(--color-surface-1)] border border-[var(--color-border)] rounded-[var(--radius-md)] py-2.5 px-3 text-sm outline-none focus:border-[var(--color-accent)]"
              />
            </div>
            {error ? (
              <p role="alert" className="text-xs text-[var(--color-error)]">
                {error}
              </p>
            ) : null}
            <button
              type="submit"
              disabled={submitting}
              className="w-full text-sm font-medium py-2.5 px-4 rounded-[var(--radius-md)] border border-[var(--color-border)] text-[var(--color-text-primary)] hover:bg-[var(--color-surface-2)] disabled:opacity-60"
            >
              {submitting ? 'Signing in…' : 'Sign in'}
            </button>
          </form>
        ) : null}

        <p className="text-[var(--color-text-tertiary)] text-xs text-center mt-8">
          Connect · Participate · Grow
        </p>
      </motion.div>
    </div>
  );
}
